import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
import { Trophy, Medal, User, ArrowLeft, Calendar, Globe } from 'lucide-react';
import { cn } from '../lib/utils';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import GalleryPhoto from './GalleryPhoto';
import AvatarFrame from './AvatarFrame';

interface ScoreEntry {
  uid: string;
  displayName: string;
  photoURL: string;
  score: number;
  level?: number;
  avatarId?: string;
  timestamp: string;
}

interface LeaderboardProps {
  onBack: () => void;
}

export default function Leaderboard({ onBack }: LeaderboardProps) {
  const [mode, setMode] = useState<'pemdas' | 'shellcrush' | 'starburst' | 'goldrush'>('pemdas');
  const [scores, setScores] = useState<ScoreEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      try {
        const monthLabel = format(new Date(), 'yyyy-MM');
        const modesToQuery = mode === 'pemdas' ? ['pemdas', 'duel'] :
                            mode === 'shellcrush' ? ['shellcrush', 'sum10'] :
                            mode === 'starburst' ? ['starburst', 'apple'] : [mode];
        
        const q = query(
          collection(db, 'scores'),
          where('mode', 'in', modesToQuery),
          where('monthLabel', '==', monthLabel),
          orderBy('score', 'desc'),
          limit(100)
        );
        
        const snap = await getDocs(q);
        const allFetchedData = snap.docs.map(doc => doc.data() as ScoreEntry);
        
        const uniqueScores: Record<string, ScoreEntry> = {};
        allFetchedData.forEach(entry => {
          if (!uniqueScores[entry.uid] || entry.score > uniqueScores[entry.uid].score) {
            uniqueScores[entry.uid] = entry;
          }
        });

        const sortedData = Object.values(uniqueScores).sort((a, b) => b.score - a.score);
        setScores(sortedData.slice(0, 50));
      } catch (err) {
        console.error("Error fetching scores:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [mode]);

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-slate-900 relative overflow-hidden font-num">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#22d3ee 2px, transparent 2px)', backgroundSize: '32px 32px' }} />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-6 flex flex-col gap-4 relative z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-3 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:bg-white transition-all active:translate-y-1 shadow-sm"
          >
            <ArrowLeft className="w-6 h-6 text-slate-400" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-3xl font-num font-black uppercase tracking-tight text-slate-900 leading-none">Rankings</h1>
            <p className="text-[10px] font-black text-cyan-600 uppercase tracking-[0.2em] mt-1">{format(new Date(), 'MMMM yyyy')}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-6 pb-2 relative z-10">
        {(['pemdas', 'shellcrush', 'starburst', 'goldrush'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={cn(
              "flex-1 py-3 rounded-2xl font-black text-[9px] uppercase tracking-widest transition-all border-2",
              mode === m ? "bg-slate-900 text-white border-slate-900 shadow-md" : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
            )}
          >
            {m === 'pemdas' ? 'Cloud' : m === 'shellcrush' ? 'Shell' : m === 'starburst' ? 'Burst' : 'Rush'}
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      <div className="flex-1 overflow-y-auto px-6 pb-24 relative z-10 scrollbar-none">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading Rankings...</p>
          </div>
        ) : (
          <div className="space-y-3 py-4">
            {scores.length === 0 ? (
              <div className="py-20 text-center">
                <Trophy className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No rankings yet this month</p>
              </div>
            ) : scores.map((score, index) => (
              <div 
                key={`${score.uid}-${index}`}
                className={cn(
                  "bg-white p-4 rounded-[28px] border-2 border-slate-50 flex items-center gap-4 transition-all hover:border-slate-100 shadow-sm",
                  index === 0 && "border-yellow-200 bg-yellow-50/30",
                  index === 1 && "border-slate-200 bg-slate-50/30",
                  index === 2 && "border-orange-200 bg-orange-50/30"
                )}
              >
                <div className="w-8 flex justify-center">
                  {index < 3 ? (
                    <Medal className={cn(
                      "w-6 h-6",
                      index === 0 ? "text-yellow-500" : index === 1 ? "text-slate-400" : "text-orange-500"
                    )} />
                  ) : (
                    <span className="text-sm font-black text-slate-400">#{index + 1}</span>
                  )}
                </div>
                
                <AvatarFrame size="md">
                  <GalleryPhoto id={score.avatarId || 'g1'} size="md" className="w-full h-full" />
                </AvatarFrame>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-black text-slate-900 truncate uppercase text-sm leading-none">{score.displayName || 'Player'}</span>
                  </div>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Global Duelist</span>
                </div>

                <div className="text-right shrink-0">
                  <div className="text-xl font-num text-slate-900 leading-none">{score.score.toLocaleString()}</div>
                  <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-1">Total Score</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
